import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Plus } from 'lucide-react';
import HermesChat from '../../hermes/HermesChat';
import useHermesSession from '../../../hooks/useHermesSession';
import { AGENT_NAME } from '../../../config';
import {
  listChats, createChat, getChat, setChatTitle, touchChat, refreshChats, subscribe as subscribeChats,
} from '../../../lib/hermesChats';

// Hermes chat inside the chart-intelligence sidebar. Same per-user chat
// list as /hermes — backed by `lib/hermesChats` (localStorage index +
// server-side history on the sandbox keyed by `conversation` param).
export default function HermesAgentTab({ currentSymbol }) {
  const userId = localStorage.getItem('userId') || 'anon';
  const { baseUrl, sessionToken, sessionKey, chart, filesBase, loading, error, refresh } = useHermesSession();

  // Option A handoff: feed the chart-WS facet of this session to the chart
  // bridge (window._tvBridge, owned by TVChartContainer). Runs on first
  // engagement and on every token refresh / sandbox recycle. We also stash it
  // on window._tvChartSession so a bridge that mounts AFTER us picks it up.
  // No teardown on unmount: the bridge must survive sidebar-tab switches (this
  // tab unmounts when the user views Symbols/Patterns) — it dies with the
  // chart page, not with this tab.
  useEffect(() => {
    if (!chart?.sessionToken) return;
    const s = { baseUrl: chart.baseUrl, sessionToken: chart.sessionToken };
    window._tvChartSession = s;
    window._tvBridge?.setRemoteSession(s);
  }, [chart?.sessionToken, chart?.baseUrl]);

  // The chart bridge fires this when its WS token has expired (it can't re-mint
  // one itself). Re-fetch the session → new chart token → pushed to the bridge
  // by the effect above → bridge reconnects with valid creds.
  useEffect(() => {
    const onExpired = () => refresh();
    window.addEventListener('tvbridge:sessionexpired', onExpired);
    return () => window.removeEventListener('tvbridge:sessionexpired', onExpired);
  }, [refresh]);

  const [chats, setChats] = useState(() => listChats(userId));
  const [convId, setConvId] = useState(() => listChats(userId)[0]?.id || null);

  // Mint a default conv if user has none.
  const initRef = useRef(false);
  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;
    if (!convId) {
      const c = createChat(userId);
      setConvId(c.id);
    }
  }, [convId, userId]);

  // Keep the chat list in sync when other surfaces (the global Sidebar, the
  // standalone AgentBro page) mutate it.
  useEffect(() => subscribeChats(() => setChats(listChats(userId))), [userId]);

  // Pull the authoritative session list from Hermes when a session is live
  // (server-backed → reflects chats from any device; populates a new device).
  useEffect(() => {
    if (baseUrl && sessionToken) refreshChats(userId, baseUrl, sessionToken).catch(() => {});
  }, [baseUrl, sessionToken, userId]);

  const handleNew = () => {
    const c = createChat(userId);
    setConvId(c.id);
  };

  const titleSetRef = useRef(false);
  useEffect(() => { titleSetRef.current = false; }, [convId]);
  const handleUserMessage = (text) => {
    if (!convId) return;
    touchChat(userId, convId);
    if (titleSetRef.current) return;
    titleSetRef.current = true;
    const existing = getChat(userId, convId);
    if (existing && existing.title === 'New chat') {
      setChatTitle(userId, convId, text);
    }
  };

  const seed = currentSymbol
    ? `The user is currently looking at the chart for ${currentSymbol}. Treat this as live context for their next questions about technicals, fundamentals, analyst calls, or news.`
    : undefined;

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-gray-400">
          <Loader2 size={28} className="animate-spin text-[#4F8EF7]" />
          <div className="text-sm">Waking {AGENT_NAME}…</div>
          <div className="text-[11px] text-gray-600">first spawn can take 10–15s</div>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-sm text-gray-300 gap-3">
        <div className="text-red-400">Couldn't reach your assistant: {error}</div>
        <button onClick={refresh} className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded text-sm">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-[#1a1c2a]">
      <div className="flex items-center gap-2 px-2 py-1.5 border-b border-gray-700/50">
        <select
          value={convId || ''}
          onChange={(e) => setConvId(e.target.value)}
          className="flex-1 bg-[#0f1018] text-gray-200 text-xs rounded border border-gray-700 px-2 py-1 focus:outline-none focus:border-blue-500 truncate"
          title="Switch chat thread"
        >
          {chats.length === 0 && <option value="">No chats</option>}
          {chats.map((c) => (
            <option key={c.id} value={c.id}>{c.title}</option>
          ))}
        </select>
        <button
          onClick={handleNew}
          title="New chat"
          className="text-gray-400 hover:text-white p-1 rounded hover:bg-white/5 transition"
        >
          <Plus size={14} />
        </button>
      </div>
      <div className="flex-1 min-h-0">
        <HermesChat
          key={convId}
          baseUrl={baseUrl}
          apiKey={sessionToken}
          sessionKey={sessionKey}
          filesBase={filesBase}
          conversation={convId}
          seedContext={seed}
          placeholder={currentSymbol ? `Ask ${AGENT_NAME} about ${currentSymbol}…` : `Ask ${AGENT_NAME}…`}
          onUserMessage={handleUserMessage}
          onSessionStale={refresh}
        />
      </div>
    </div>
  );
}
